class CustomProjectExperience extends HTMLElement {
  #controller;
  #form;
  #steps = [];
  #currentStep = 1;
  #carousel;
  #slides = [];
  #dots = [];
  #currentSlide = 0;
  #autoplayTimer;
  #autoplayDelay = 7000;
  #userPaused = false;
  #interactionPaused = false;
  #reducedMotion = false;
  #submissionAccepted = false;
  #submissionInFlight = false;
  #lastPayloadSignature = '';
  #captchaErrorVisible = false;

  connectedCallback() {
    this.#controller = new AbortController();
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.classList.add('is-enhanced');

    this.#setupForm();
    this.#setupCarousel();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#stopAutoplay();
  }

  #setupForm() {
    this.#form = this.querySelector('[data-contact-form]');
    if (!this.#form || this.#submissionAccepted) return;

    this.#steps = Array.from(this.querySelectorAll('[data-form-step]'));
    if (!this.#steps.length) return;

    window.TodayMerchClientSelectionRequest?.applyToForm(this.#form);

    this.#currentStep = 1;
    this.#showStep(this.#currentStep, false);
    this.#syncTargetDate();
    this.#syncUnknownFields();

    this.#form.addEventListener('click', this.#handleFormClick, { signal: this.#controller.signal });
    this.#form.addEventListener('input', this.#handleFieldUpdate, { signal: this.#controller.signal });
    this.#form.addEventListener('change', this.#handleFieldUpdate, { signal: this.#controller.signal });
    this.#form.addEventListener('invalid', this.#handleInvalid, { capture: true, signal: this.#controller.signal });
    this.#form.addEventListener('submit', this.#handleSubmit, { signal: this.#controller.signal });
    this.#form.addEventListener('todaymerch:recaptcha-change', this.#handleCaptchaChange, { signal: this.#controller.signal });
    this.#mountCaptcha();
    const submitButton = this.#form.querySelector('[type="submit"]');
    if (submitButton) submitButton.disabled = false;
  }

  #mountCaptcha() {
    const captcha = window.TodayMerchRecaptcha;
    if (!captcha?.mount) {
      this.#showPublicError({ code: 'AUTOREPLY_CAPTCHA_UNAVAILABLE' });
      return;
    }
    captcha.mount(this.#form).catch((error) => {
      if (!this.#submissionAccepted && !this.#submissionInFlight) this.#showPublicError(error);
    });
  }

  #handleCaptchaChange = (event) => {
    if (this.#submissionAccepted || this.#submissionInFlight) return;
    if (event.detail?.error) this.#showPublicError(event.detail.error);
    else if (event.detail?.verified && this.#captchaErrorVisible) {
      const status = this.querySelector('[data-custom-project-api-status]');
      if (status) status.hidden = true;
      this.#captchaErrorVisible = false;
    }
  };

  #handleFormClick = (event) => {
    if (this.#submissionInFlight || this.#submissionAccepted) return;
    const nextButton = event.target.closest('[data-next-step]');
    if (nextButton) {
      const panel = this.#steps[this.#currentStep - 1];
      if (!this.#validatePanel(panel)) return;
      this.#showStep(Math.min(this.#currentStep + 1, this.#steps.length), true);
      return;
    }

    const previousButton = event.target.closest('[data-previous-step]');
    if (!previousButton) return;
    this.#showStep(Math.max(this.#currentStep - 1, 1), true);
  };

  #handleFieldUpdate = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) return;

    if (field.matches('[data-no-date]')) this.#syncTargetDate();
    if (field.matches('[data-unknown-field]')) this.#syncUnknownFields();
    this.#validateRequiredText(field);
    if (field.checkValidity()) field.removeAttribute('aria-invalid');
  };

  #handleSubmit = (event) => {
    event.preventDefault();
    if (this.#submissionInFlight || this.#submissionAccepted) return;

    for (const [index, panel] of this.#steps.entries()) {
      const invalidField = this.#findInvalidField(panel);
      if (!invalidField) continue;

      this.#showStep(index + 1, true);
      this.#revealField(invalidField);
      invalidField.setAttribute('aria-invalid', 'true');
      invalidField.reportValidity();
      invalidField.focus();
      return;
    }

    if (!this.#getAutoReplyApiBase()) {
      const error = new Error('Custom project API is unavailable');
      error.code = 'AUTOREPLY_SECURITY_UNAVAILABLE';
      this.#showPublicError(error);
      return;
    }

    const captcha = window.TodayMerchRecaptcha;
    const captchaToken = captcha?.getReadyToken(this.#form);
    if (!captchaToken) {
      this.#showPublicError(captcha?.getError(this.#form) || {
        code: captcha ? 'AUTOREPLY_CAPTCHA_REQUIRED' : 'AUTOREPLY_CAPTCHA_UNAVAILABLE'
      });
      this.#mountCaptcha();
      return;
    }

    this.#showStep(this.#steps.length, false);
    const status = this.querySelector('[data-custom-project-api-status]');
    if (status) status.hidden = true;
    const leadId = this.#getFieldValue('contact[Lead ID]');
    this.#submissionInFlight = true;
    this.#setSubmitting(true);
    this.#sendCustomProject(captchaToken)
      .then((result) => {
        this.#submissionAccepted = true;
        window.TodayMerchClientSelectionRequest?.clear(this.#form);
        this.#showSuccess(result);
        document.dispatchEvent(new CustomEvent('todaymerch:contact-succeeded', { detail: { lead_id: leadId } }));
      })
      .catch((error) => {
        captcha.reset(this.#form);
        this.#showPublicError(error);
      })
      .finally(() => {
        this.#submissionInFlight = false;
        this.#setSubmitting(false);
      });
  };

  #setSubmitting(isSubmitting) {
    this.#form.setAttribute('aria-busy', String(isSubmitting));
    for (const button of this.#form.querySelectorAll('button')) {
      button.disabled = isSubmitting || this.#submissionAccepted;
    }
    const label = this.#form.querySelector('[data-submit-label]');
    if (label) label.textContent = isSubmitting ? this.#form.dataset.submittingLabel : this.#form.dataset.submitLabel;
  }

  #showSuccess(result) {
    const success = this.querySelector('[data-form-success]');
    if (!success) return;
    const message = success.querySelector('[data-success-message]');
    if (message && result.queued === true) message.textContent = success.dataset.queuedText;
    const reference = success.querySelector('[data-success-reference]');
    const requestId = result.reference || result.requestId || result.request_id;
    if (reference && typeof requestId === 'string' && requestId && requestId.length <= 32) {
      reference.textContent = `${success.dataset.referenceLabel} ${requestId}`;
      reference.hidden = false;
    }
    this.#form.hidden = true;
    success.hidden = false;
    success.setAttribute('data-today-merch-lead-success', 'contact_submitted');
    this.querySelector('.custom-project-form')?.setAttribute('aria-labelledby', success.dataset.titleId);
    success.focus({ preventScroll: true });
    success.scrollIntoView({ behavior: this.#reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  #getAutoReplyApiBase() {
    return String(this.#form?.dataset.autoReplyApiBase || '').replace(/\/$/, '');
  }

  #getFieldValue(name) {
    const field = this.#form?.elements.namedItem(name);
    return field && typeof field.value === 'string' ? field.value.trim() : '';
  }

  #getRawFieldValue(name) {
    const field = this.#form?.elements.namedItem(name);
    return field && typeof field.value === 'string' ? field.value : '';
  }

  #showPublicError(error) {
    this.#captchaErrorVisible = String(error?.code || '').startsWith('AUTOREPLY_CAPTCHA_');
    if (error?.code === 'AUTOREPLY_RECIPIENT_UNDELIVERABLE') {
      if (this.#form?.dataset) delete this.#form.dataset.autoreplyRequestId;
      this.#showPublicMessage(this.#form.dataset.recipientError, true);
      return;
    }
    const publicApi = window.TodayMerchPublicApi;
    const locale = this.#form?.dataset.locale || document.documentElement.lang || 'fr';
    const submissionError = !error?.code || ['AUTOREPLY_REQUEST_FAILED', 'AUTOREPLY_DELIVERY_FAILED'].includes(error.code);
    const message = submissionError ? this.#form.dataset.submitError : publicApi?.errorMessage(
      { code: error?.code || 'AUTOREPLY_REQUEST_FAILED' },
      locale
    );
    this.#showPublicMessage(message || this.#form.dataset.submitError, true);
  }

  #showPublicMessage(message, isError = true) {
    const status = this.querySelector('[data-custom-project-api-status]');
    if (!status) return;
    status.textContent = message || this.#form.dataset.submitError || '';
    status.hidden = false;
    if (typeof status.setAttribute === 'function') status.setAttribute('aria-invalid', isError ? 'true' : 'false');
    status.focus?.({ preventScroll: true });
    status.scrollIntoView?.({ behavior: this.#reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
  }

  #buildAutoReplyPayload() {
    const fields = {};
    const formData = new FormData(this.#form);

    for (const [name, rawValue] of formData.entries()) {
      if (typeof rawValue !== 'string') continue;
      const value = rawValue;
      if (!value.trim()) continue;

      const match = name.match(/^contact\[(.+)]$/);
      if (!match) continue;

      const key = match[1];
      const fieldCodes = {
        'Request type': 'request_type',
        'Project type': 'project_type',
        Budget: 'budget',
        Quantity: 'quantity',
        'Target date': 'target_date',
        'Delivery country': 'delivery_country',
        'No specific deadline': 'no_specific_deadline',
        'Multiple delivery addresses': 'multiple_delivery_addresses',
        'First name': 'first_name',
        'Last name': 'last_name',
        name: 'name',
        Company: 'company',
        email: 'email',
        Phone: 'phone',
        body: 'message'
      };
      const enumCodes = {
        'Custom project': 'custom_project',
        'Welcome pack': 'welcome_pack',
        'Event or trade show': 'event_trade_show',
        'Client gifts': 'client_gifts',
        'Employee gifts': 'employee_gifts',
        'Apparel collection': 'apparel_collection',
        'Bespoke objects': 'bespoke_objects',
        'Packaging or gift boxes': 'packaging_gift_boxes',
        'Campaign or influence': 'campaign_influence',
        Other: 'other',
        Yes: 'yes'
      };
      const code = fieldCodes[key] || key;
      let normalizedValue = value;
      if (enumCodes[value] && ['request_type', 'project_type', 'no_specific_deadline', 'multiple_delivery_addresses'].includes(code)) {
        normalizedValue = { value: enumCodes[value], valueCode: enumCodes[value] };
      } else if (code === 'target_date') {
        normalizedValue = { value, format: 'date' };
      } else if (['quantity', 'budget'].includes(code) && Number.isFinite(Number(value))) {
        normalizedValue = { value: Number(value), format: 'number' };
      }
      if (fields[code] && typeof fields[code] === 'string' && typeof normalizedValue === 'string') {
        fields[code] = `${fields[code]}\n${normalizedValue}`;
      } else {
        fields[code] = normalizedValue;
      }
    }

    const payload = {
      type: this.#form.dataset.autoReplyType || 'custom_project',
      page_url: this.#form.dataset.pageUrl || window.location.href,
      contact: {
        ...(this.#getFieldValue('contact[name]')
          ? { name: this.#getRawFieldValue('contact[name]') }
          : {
            first_name: this.#getRawFieldValue('contact[First name]'),
            last_name: this.#getRawFieldValue('contact[Last name]')
          }),
        email: this.#getFieldValue('contact[email]'),
        company: this.#getRawFieldValue('contact[Company]')
      },
      fields: window.TodayMerchClientSelectionRequest?.appendToPayload(this.#form, fields) || fields,
      website: this.#getRawFieldValue('website')
    };
    const locale = this.#form.dataset.locale || document.documentElement.lang || 'fr';
    const localizedPayload = window.TodayMerchPublicApi?.withLocale(payload, locale) || { ...payload, locale };
    const signature = JSON.stringify(localizedPayload);
    if (this.#lastPayloadSignature && this.#lastPayloadSignature !== signature) {
      delete this.#form.dataset.autoreplyRequestId;
    }
    this.#lastPayloadSignature = signature;
    if (!this.#form.dataset.autoreplyRequestId && window.crypto?.getRandomValues) {
      const randomBytes = window.crypto.getRandomValues(new Uint8Array(10));
      const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
      const characters = Array.from(randomBytes, (byte) => alphabet[byte & 31]).join('');
      this.#form.dataset.autoreplyRequestId = `TM-${characters.slice(0, 5)}-${characters.slice(5)}`;
    }
    if (!this.#form.dataset.autoreplyRequestId) {
      this.#form.dataset.autoreplyRequestId = `TM-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }
    return {
      ...localizedPayload,
      ...window.TodayMerchClientSelectionRequest?.requestMetadata(this.#form),
      request_id: this.#form.dataset.autoreplyRequestId,
    };
  }

  #runSubmissionTask(task) {
    const configured = Number(this.#form?.dataset.autoReplyTimeoutMs);
    const timeoutMs = Number.isFinite(configured) && configured >= 1 && configured <= 30000 ? configured : 20000;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let active = true;
    let timeout;
    const timedOut = () => {
      const error = new Error('Custom project submission timed out');
      error.code = 'AUTOREPLY_REQUEST_FAILED';
      return error;
    };
    return new Promise((resolve, reject) => {
      const finish = (callback, value) => {
        if (!active) return;
        active = false;
        window.clearTimeout(timeout);
        callback(value);
      };
      timeout = window.setTimeout(() => {
        if (controller) controller.abort();
        finish(reject, timedOut());
      }, timeoutMs);
      Promise.resolve()
        .then(() => task({ signal: controller?.signal, isActive: () => active, timedOut }))
        .then((value) => finish(resolve, value), (error) => finish(reject, error));
    });
  }

  #sendCustomProject(captchaToken) {
    const apiBase = this.#getAutoReplyApiBase();
    if (!apiBase) return Promise.resolve(null);
    return this.#runSubmissionTask(async ({ signal, isActive, timedOut }) => {
      const response = await fetch(`${apiBase}/forms/custom-project`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...this.#buildAutoReplyPayload(), recaptcha_token: captchaToken }),
        signal
      });
      const text = await response.text();
      if (!isActive()) throw timedOut();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {}
      if (!response.ok) {
        throw window.TodayMerchPublicApi?.responseError(data, this.#form?.dataset.locale)
          || new Error('Custom project submission unavailable');
      }
      const client = data?.client || {};
      const team = data?.team || {};
      const accepted = data?.ok === true
        && data?.accepted === true
        && data?.submission?.accepted === true
        && (team.sent === true || team.queued === true)
        && (client.sent === true || client.queued === true);
      if (!accepted) {
        const error = new Error('Custom project submission was not confirmed');
        error.code = data?.code || 'AUTOREPLY_DELIVERY_FAILED';
        throw error;
      }
      return { ...data, queued: client.queued === true || team.queued === true };
    });
  }

  #handleInvalid = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) return;

    field.setAttribute('aria-invalid', 'true');
    this.#revealField(field);
    const panel = field.closest('[data-form-step]');
    const panelIndex = this.#steps.indexOf(panel);
    if (panelIndex >= 0 && panelIndex + 1 !== this.#currentStep) this.#showStep(panelIndex + 1, true);
  };

  #showStep(stepNumber, shouldFocus) {
    this.#currentStep = stepNumber;

    for (const [index, panel] of this.#steps.entries()) {
      panel.hidden = index + 1 !== stepNumber;
    }

    const indicators = this.querySelectorAll('[data-step-indicator]');
    for (const [index, indicator] of Array.from(indicators).entries()) {
      const indicatorStep = index + 1;
      indicator.classList.toggle('is-complete', indicatorStep < stepNumber);
      if (indicatorStep === stepNumber) {
        indicator.setAttribute('aria-current', 'step');
      } else {
        indicator.removeAttribute('aria-current');
      }
    }

    const currentPanel = this.#steps[stepNumber - 1];
    const legend = currentPanel?.querySelector('.custom-project-form__legend');
    const announcer = this.querySelector('[data-step-announcer]');
    if (announcer && legend) {
      announcer.textContent = '';
      requestAnimationFrame(() => {
        announcer.textContent = `${stepNumber} / ${this.#steps.length} - ${legend.textContent.trim()}`;
      });
    }

    if (!shouldFocus || !legend) return;
    legend.focus({ preventScroll: true });
    this.querySelector('.custom-project-form')?.scrollIntoView({
      behavior: this.#reducedMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  }

  #validatePanel(panel) {
    const invalidField = this.#findInvalidField(panel);
    if (!invalidField) return true;

    invalidField.setAttribute('aria-invalid', 'true');
    invalidField.reportValidity();
    invalidField.focus();
    return false;
  }

  #findInvalidField(panel) {
    const controls = panel?.querySelectorAll('input, select, textarea') ?? [];
    for (const control of controls) {
      this.#validateRequiredText(control);
      if (control.disabled || control.checkValidity()) continue;
      return control;
    }
    return null;
  }

  #validateRequiredText(field) {
    if (!field.required || typeof field.setCustomValidity !== 'function') return;
    if (field.dataset.clientSelectionLengthError && field.maxLength > 0 && field.value.length > field.maxLength) {
      field.setCustomValidity(field.dataset.clientSelectionLengthError);
      return;
    }
    field.setCustomValidity(field.value.trim() ? '' : this.#form.dataset.requiredError || '');
  }

  #revealField(field) {
    const details = field.closest?.('details');
    if (details) details.open = true;
  }

  #syncUnknownFields() {
    for (const checkbox of this.#form.querySelectorAll('[data-unknown-field]')) {
      const key = checkbox.dataset.unknownField;
      if (!['budget', 'quantity'].includes(key)) continue;
      const field = this.#form.querySelector(`[data-optional-value="${key}"]`);
      if (!field) continue;
      field.disabled = checkbox.checked;
      if (checkbox.checked) field.removeAttribute('aria-invalid');
    }
  }

  #syncTargetDate() {
    const noDate = this.querySelector('[data-no-date]');
    const targetDate = this.querySelector('[data-target-date]');
    if (!noDate || !targetDate) return;

    targetDate.disabled = noDate.checked;
    if (noDate.checked) targetDate.value = '';
  }

  #setupCarousel() {
    this.#carousel = this.querySelector('[data-proof-carousel]');
    if (!this.#carousel) return;

    this.#slides = Array.from(this.#carousel.querySelectorAll('[data-proof-slide]'));
    this.#dots = Array.from(this.#carousel.querySelectorAll('[data-proof-dot]'));
    if (!this.#slides.length) return;

    const configuredDelay = Number.parseInt(this.#carousel.dataset.autoplayMs, 10);
    if (Number.isFinite(configuredDelay)) this.#autoplayDelay = Math.max(configuredDelay, 5000);

    this.#showSlide(0, false);
    if (this.#slides.length < 2) return;

    this.#carousel.addEventListener('click', this.#handleCarouselClick, { signal: this.#controller.signal });
    this.#carousel.addEventListener('mouseenter', this.#pauseForInteraction, { signal: this.#controller.signal });
    this.#carousel.addEventListener('mouseleave', this.#resumeAfterInteraction, { signal: this.#controller.signal });
    this.#carousel.addEventListener('focusin', this.#pauseForInteraction, { signal: this.#controller.signal });
    this.#carousel.addEventListener('focusout', this.#handleCarouselFocusOut, { signal: this.#controller.signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal: this.#controller.signal });

    if (!this.#reducedMotion && !window.Shopify?.designMode) this.#startAutoplay();
  }

  #handleCarouselClick = (event) => {
    const dot = event.target.closest('[data-proof-dot]');
    if (dot) {
      const index = Number.parseInt(dot.dataset.proofDot, 10);
      if (Number.isFinite(index)) this.#showSlide(index, true);
      this.#restartAutoplay();
      return;
    }

    const pauseButton = event.target.closest('[data-proof-pause]');
    if (!pauseButton) return;

    this.#userPaused = !this.#userPaused;
    pauseButton.setAttribute('aria-pressed', String(this.#userPaused));
    pauseButton.setAttribute('aria-label', this.#userPaused ? pauseButton.dataset.playLabel : pauseButton.dataset.pauseLabel);
    const icon = pauseButton.querySelector('[data-pause-icon]');
    if (icon) icon.textContent = this.#userPaused ? '▶' : 'Ⅱ';

    if (this.#userPaused) {
      this.#stopAutoplay();
      this.#setCarouselLive('polite');
    } else {
      this.#setCarouselLive('off');
      this.#startAutoplay();
    }
  };

  #showSlide(index, userInitiated) {
    this.#currentSlide = (index + this.#slides.length) % this.#slides.length;

    for (const [slideIndex, slide] of this.#slides.entries()) {
      slide.setAttribute('aria-hidden', String(slideIndex !== this.#currentSlide));
    }

    for (const [dotIndex, dot] of this.#dots.entries()) {
      dot.setAttribute('aria-pressed', String(dotIndex === this.#currentSlide));
    }

    if (userInitiated) this.#setCarouselLive('polite');
  }

  #pauseForInteraction = () => {
    this.#interactionPaused = true;
    this.#stopAutoplay();
  };

  #resumeAfterInteraction = () => {
    this.#interactionPaused = false;
    this.#setCarouselLive(this.#userPaused ? 'polite' : 'off');
    this.#startAutoplay();
  };

  #handleCarouselFocusOut = (event) => {
    if (this.#carousel.contains(event.relatedTarget)) return;
    this.#resumeAfterInteraction();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stopAutoplay();
    } else {
      this.#startAutoplay();
    }
  };

  #setCarouselLive(value) {
    this.#carousel?.querySelector('[data-proof-slides]')?.setAttribute('aria-live', value);
  }

  #startAutoplay() {
    if (this.#reducedMotion || this.#userPaused || this.#interactionPaused || document.hidden || this.#slides.length < 2) return;
    this.#stopAutoplay();
    this.#autoplayTimer = window.setInterval(() => {
      this.#setCarouselLive('off');
      this.#showSlide(this.#currentSlide + 1, false);
    }, this.#autoplayDelay);
  }

  #stopAutoplay() {
    if (!this.#autoplayTimer) return;
    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  }

  #restartAutoplay() {
    this.#stopAutoplay();
    this.#startAutoplay();
  }
}

if (!customElements.get('custom-project-experience')) {
  customElements.define('custom-project-experience', CustomProjectExperience);
}
